import type { SearchResult } from "./types";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

type Props = {
  results: SearchResult[];
  selected: SearchResult | null;
  canPublishKb: boolean;
  publishWorking: boolean;
  deleteWorking: boolean;
  confirmPublishSlug: string | null;
  confirmDeleteSlug: string | null;
  quickAuditEnabled: boolean;
  onLoadArticle: (slug: string, title: string) => void;
  onOpenEditorMode: () => void;
  onConfirmPublish: (slug: string | null) => void;
  onConfirmDelete: (slug: string | null) => void;
  onPublishDraft: (slug: string) => void;
  onSoftDelete: (slug: string) => void;
  showDevRescopePill?: boolean;
  devRescopeBusy?: boolean;
  onDevRescopeArticle?: (result: SearchResult) => void | Promise<void>;
  /**
   * Tailwind class string applied to the outer scroll container. Defaults to
   * `max-h-[32rem] overflow-y-auto pr-1` for the mobile/standalone case;
   * desktop layouts pass `lg:max-h-none` to drop the inner-scroll constraint
   * once the column has room to grow.
   */
  scrollContainerClassName?: string;
};

/**
 * Results-list rendering extracted from `KbLibrarySection.tsx` so that the
 * parent can wrap controls + results in a 2-column desktop layout without
 * pushing the parent file over the 260-line soft threshold defined in
 * `.cursor/rules/file-size-hygiene.mdc`.
 */
export function KbLibrarySectionResultsList(props: Props) {
  const scrollClass = props.scrollContainerClassName ?? "max-h-[32rem] overflow-y-auto pr-1";
  return (
    <div {...{ [FIREFLY_TARGET_ATTR]: "kb-results-list" }} className={`space-y-2 ${scrollClass}`}>
      {props.results.length ? props.results.map((result) => (
        <div
          key={result.id}
          className={
            "rounded-xl border px-3 py-3 transition " +
            (props.selected?.id === result.id
              ? "border-indigo-300 bg-indigo-50 dark:border-indigo-700 dark:bg-indigo-950/35"
              : "border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-white dark:border-zinc-700 dark:bg-zinc-900/40 dark:hover:border-zinc-600 dark:hover:bg-zinc-800/80")
          }
        >
          {props.confirmPublishSlug === result.slug ? (
            <div className="flex flex-wrap items-center gap-2 text-xs text-emerald-900 dark:text-emerald-100">
              <span className="font-medium">Publish &ldquo;{result.title}&rdquo; without manual review?</span>
              <button type="button" className="rounded-lg border border-emerald-500 bg-emerald-100 px-2 py-1 font-semibold text-emerald-900 disabled:opacity-50 dark:border-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-100" onClick={() => props.onPublishDraft(result.slug)} disabled={props.publishWorking}>
                {props.publishWorking ? "Publishing…" : "Confirm publish"}
              </button>
              <button type="button" className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-gray-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200" onClick={() => props.onConfirmPublish(null)}>
                Cancel
              </button>
            </div>
          ) : props.confirmDeleteSlug === result.slug ? (
            <div className="flex flex-wrap items-center gap-2 text-xs text-red-900 dark:text-red-200">
              <span className="font-medium">Delete "{result.title}"? This cannot be undone without admin access.</span>
              <button type="button" className="rounded-lg border border-red-400 bg-red-100 px-2 py-1 font-semibold text-red-900 disabled:opacity-50 dark:border-red-700 dark:bg-red-950/50 dark:text-red-100" onClick={() => props.onSoftDelete(result.slug)} disabled={props.deleteWorking}>
                {props.deleteWorking ? "Deleting…" : "Confirm delete"}
              </button>
              <button type="button" className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-gray-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200" onClick={() => props.onConfirmDelete(null)}>
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-3">
              <button
                type="button"
                className="min-w-0 flex-1 text-left"
                onClick={() => {
                  props.onLoadArticle(result.slug, result.title);
                  props.onOpenEditorMode();
                }}
              >
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">{result.title}</div>
                    <div className="text-xs text-gray-500 mt-1 dark:text-zinc-400">{result.slug}</div>
                  </div>
                  <span className={
                    "shrink-0 text-[11px] rounded-full px-2 py-0.5 border " +
                    (result.status === "draft"
                      ? "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
                      : "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200")
                  }>
                    {result.status}
                  </span>
                </div>
                <div className="mt-2 text-xs text-gray-600 dark:text-zinc-400">
                  audience {result.audience} • scope {result.scope ?? "(none)"} • type {result.doc_type ?? "(none)"}
                </div>
                <div className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                  edited by {result.last_edited_by ?? "unknown"}
                </div>
                {props.quickAuditEnabled && typeof result.total_chunk_chars === "number" ? (
                  <div className="mt-1 text-xs text-gray-600 dark:text-zinc-400">
                    chunk chars {result.total_chunk_chars} • chunks {result.chunk_count ?? 0} • risk {result.risk ?? "unknown"}
                  </div>
                ) : null}
              </button>
              <div className="flex shrink-0 flex-col items-end gap-1.5">
                {props.canPublishKb && result.status === "draft" ? (
                  <button
                    type="button"
                    className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-800 hover:bg-emerald-100 disabled:opacity-50 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200 dark:hover:bg-emerald-900/50"
                    onClick={(event) => {
                      event.stopPropagation();
                      props.onConfirmPublish(result.slug);
                      props.onConfirmDelete(null);
                    }}
                    disabled={props.publishWorking}
                    title="Publish this draft"
                  >
                    Publish
                  </button>
                ) : null}
                {props.showDevRescopePill ? (
                  <a
                    href="#kb-dev-rescope-panel"
                    className="rounded-full border border-amber-400 bg-amber-50 px-2 py-0.5 text-center text-[11px] font-medium text-amber-950 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-600 dark:bg-amber-950/45 dark:text-amber-100 dark:hover:bg-amber-900/50"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      if (props.devRescopeBusy) return;
                      void props.onDevRescopeArticle?.(result);
                    }}
                    aria-busy={props.devRescopeBusy || undefined}
                  >
                    {props.devRescopeBusy ? "Dev…" : "Dev rescope"}
                  </a>
                ) : null}
                <button
                  type="button"
                  className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] text-red-700 hover:bg-red-100 disabled:opacity-50 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300 dark:hover:bg-red-950/60"
                  onClick={(event) => {
                    event.stopPropagation();
                    props.onConfirmDelete(result.slug);
                    props.onConfirmPublish(null);
                  }}
                  disabled={props.deleteWorking}
                  title="Delete article"
                >
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      )) : (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-500 dark:border-zinc-600 dark:bg-zinc-900/40 dark:text-zinc-400">
          Search the library or load drafts to start working.
        </div>
      )}
    </div>
  );
}
